import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { UploadOutput, UploadInput, UploadFile, humanizeBytes, UploadStatus } from 'ngx-uploader';
import {CompleterItem, CompleterService, RemoteData} from 'ng2-completer';
import {Folder} from 'storyboard-renderer';
import {environment} from '../../../environments/environment';
import {MediaLibraryService} from '../shared/media-library.service';
import {AuthService} from "../../auth/auth.service";
import {Media} from "storyboard-renderer";
import {AlertService} from "../../shared/alert/alert.service"
import {Alert} from "../../shared/alert/Alert"
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'app-upload-media',
  templateUrl: './upload-media.component.html',
  styleUrls: ['./upload-media.component.scss']
})

export class UploadMediaComponent implements OnInit {

  formData: FormData;
  files: UploadFile[];
  uploadInput: EventEmitter<UploadInput>;
  humanizeBytes: Function;
  dragOver: boolean;
  remaining = '';
  total = '';
  speed = '';
  uploadPercent = 0;
  done = UploadStatus.Done;
  allUploadDone = false;
  selectedFolder: Folder;
  folderSearchQuery = '';
  protected dataService: RemoteData;

  uploading = false;
  noResultsForFolder = false;

  @Input() userId = null;

  @Output() selectMediaEvent = new EventEmitter();
  @Output() uploadDoneEvent = new EventEmitter();

  private endpoint = environment.apiBaseUrl + '/api/v1.0/';

  constructor(
    private completerService: CompleterService,
    private mediaLibraryService: MediaLibraryService,
    private authService: AuthService,
    protected alertService: AlertService,
    protected route: ActivatedRoute
  ) {
    this.files = []; // local uploading files array
    this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader
    this.humanizeBytes = humanizeBytes;
  }

  ngOnInit() {

    const userId = this.userId || 'me';
    this.dataService = this.completerService.remote(this.endpoint + 'user/' + userId + '/media_folder?query=', 'title', 'title');
    this.dataService.dataField('data');

    this.dataService.subscribe((completerItems: CompleterItem[]) => {
      this.noResultsForFolder = completerItems.length === 0;
    });
  }

  checkFileFormat(file): Boolean {

    if (file.type.match('image\/(jpeg|png)')) {
      return true;
    }

    return false;
  }

  protected displayInvalidFileFormatWarning(){
    if (this.alertService){
      this.alertService.danger(new Alert('Only PNG and JPG images are supported.'));
    }
  }

  onUploadOutput(output: UploadOutput): void {

    if (output.type === 'allAddedToQueue') { // when all files added in queue

    } else if (output.type === 'addedToQueue') {

      if (this.checkFileFormat(output.file)) {
        this.files = [];

        this.files.push(output.file);
      }else {
        this.displayInvalidFileFormatWarning();
      }
    } else if (output.type === 'uploading') {
      // update current data in files array for uploading file
      const index = this.files.findIndex(file => file.id === output.file.id);
      this.files[index] = output.file;

      let _total = 0;
      let _remaining = 0;

      for (let i = 0; i < this.files.length; i++) {
        _total += this.files[i].size;
        _remaining += this.files[i].size * (this.files[i].progress.data.percentage / 100);
      }

      this.total = this.humanizeBytes(_total);
      this.remaining = this.humanizeBytes(_remaining);
      this.uploadPercent = _remaining / _total * 100;
      this.speed = this.files[index].progress.data.speedHuman;

    } else if (output.type === 'removed') {
      // remove file from array when removed
      this.files = this.files.filter((file: UploadFile) => file !== output.file);
    } else if (output.type === 'dragOver') { // drag over event
      this.dragOver = true;
    } else if (output.type === 'dragOut') { // drag out event
      this.dragOver = false;
    } else if (output.type === 'drop') { // on drop event
      this.dragOver = false;
    } else if (output.type === 'done') {

      if (output.file['responseStatus'] && output.file['responseStatus'] >= 400){
        /**
         * sikertelen feltöltés
         */
        this.displayFailedUploadMessage(output.file);
        /**
         * hogy visszaálljon a UI alapállapotba:
         */
         if (this.allUploadIsDone()) {
           this.allUploadDone = true;
         }
        this.files = [];
        this.uploading = false;
      }else {
        /**
         * sikeres feltöltés
         */
        if (this.allUploadIsDone()) {
          if (this.files.length === 1) {
            this.selectMediaEvent.next(Media.fromJson(this.files[0].response));
          } else {
            this.uploadDoneEvent.next(this.files);
          }
          this.allUploadDone = true;
        }
      }

    }
  }

  protected displayFailedUploadMessage(file){
    if (!this.alertService || !file || !file.responseStatus){
      return;
    }

    switch(file.responseStatus){
      case 415:
        this.alertService.danger(new Alert('Only PNG and JPG images are supported.'));
      break;
      default:
        this.alertService.danger(new Alert('Upload failed. Please try again later.'));
      break;
    }
  }

  private allUploadIsDone(): Boolean {
    for (let i = 0; i < this.files.length; i++) {
      if (this.files[i].progress.status !== UploadStatus.Done) {
        return false;
      }
    }
    return true;
  }

  startUpload(): void {  // manually start uploading

    if (!this.files.length) {
      return;
    }

    this.uploading = true;

    const folderData = {};

    if (this.selectedFolder) {
      folderData['folder[id]'] = this.selectedFolder.id.toString();
    } else if (this.folderSearchQuery !== '') {
      folderData['folder[title]'] = this.folderSearchQuery;
    }

    const userId = this.userId || this.authService.getCurrentUser().id;

    const event: UploadInput = {
      type: 'uploadAll',
      headers: {'Authorization': 'Bearer ' + this.authService.getCurrentUserToken()},
      url: this.endpoint + 'user/' + userId + '/media',
      method: 'POST',
      data: folderData,
      concurrency: 1 // set sequential uploading of files with concurrency 1
    };

    this.uploadInput.emit(event);
  }

  cancelUpload(id: string): void {
    this.uploadInput.emit({ type: 'cancel', id: id });
  }

  protected onFolderSelected(selectedItem: CompleterItem) {
    if (selectedItem) {
      this.selectedFolder = selectedItem.originalObject as Folder;
    }
  }

  createFolder() {
    const folder: Folder = new Folder(null, this.folderSearchQuery);
    this.mediaLibraryService.createFolder(folder, {userId: this.userId}).then(() => {
      console.log('success create');
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/media-library/upload-media/upload-media.component.ts